import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { EContactCustomId } from "@rentiohq/shared-frontend/dist/redux/contact/contact.types";
import * as formSelectors from "@rentiohq/shared-frontend/dist/redux/form/form.selectors";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import * as contractSelectors from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.selectors";
import * as documentHooks from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.hooks";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import * as rentRegistrationHooks from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.hooks";
import * as templateDocumentsActionsV2 from "@rentiohq/shared-frontend/dist/reduxV2/templateDocuments/templateDocument.actions";
import * as templateDocumentHooks from "@rentiohq/shared-frontend/dist/reduxV2/templateDocuments/templateDocument.hooks";
import { ERecipientType } from "@rentiohq/shared-frontend/dist/reduxV2/templateDocuments/templateDocument.types";
import { EDocumentPackageTypes } from "@rentiohq/shared-frontend/dist/types/documentPackage.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import {
  Card,
  Loading,
  MultiStepForm,
  Page,
} from "@rentiohq/web-shared/dist/components";
import utils from "@rentiohq/web-shared/dist/utils";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import {
  BooleanParam,
  NumberParam,
  StringParam,
  createEnumParam,
} from "serialize-query-params";
import createDocumentPackageForm from "../../forms/createDocumentPackage";
import {
  isPerformingSubmitActionSelector,
  performingSubmitActionErrorSelector,
  performingSubmitActionResultSelector,
  submitActionCreator,
} from "../../forms/createDocumentPackage/schema.createDocumentPackage.actions";
import {
  EField,
  IExtraData,
} from "../../forms/createDocumentPackage/schema.createDocumentPackage.types";
import { ts } from "../../services";
import { getBreadcrumb } from "./DocumentPackageForm.utils";

const generatedFormId = generateFormId();
const FORM_ID = `document-package-${generatedFormId}`;

export const DocumentPackageForm = React.memo(() => {
  const navigate = useNavigate();

  // Redux
  const dispatch = useDispatch();

  const [params] = useQueryParams({
    propertyId: NumberParam,
    contractId: StringParam,
    documentIdToSign: StringParam,
    documentPackageType: createEnumParam(Object.values(EDocumentPackageTypes)),
    sendToTenant: BooleanParam,
    templateDocumentId: StringParam,
  });
  const { propertyId } = params;
  const { contractId } = params;
  const documentId = params?.documentIdToSign;
  const documentPackageType = params?.documentPackageType;
  const sendToTenant = params?.sendToTenant;

  const contactMeMaster = useSelector((state: IRootStore) =>
    contactSelectors.getContactMeMaster(state),
  );

  const { detail: contract } = contractHooks.useDetail({
    shouldRefetch: false,
    id: contractId,
  });

  const { detail: property } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: propertyId,
  });

  const { detail: document } = documentHooks.useDetail({
    shouldRefetch: false,
    id: documentId,
  });

  // INFO: v2 returns every field on the detail endpoint, so the separate full-payload read the
  // v1 module needed is gone
  const { detail: templateDocument, refetch: refetchTemplateDocument } =
    templateDocumentHooks.useDetail({
      shouldRefetch: true,
      id: document?.templateDocumentId,
    });

  // The lease the auto-registration step reasons about is the form's own
  // answer, which the contract step can still change - not just the query
  // param the scene was opened with.
  const selectedContractId = useSelector(
    (state: IRootStore) =>
      formSelectors.mergedPartialFormDataSelector(state, FORM_ID)?.[
        EField.Contract
      ] as string | undefined,
  );

  const selectedContract = useSelector((state: IRootStore) =>
    contractSelectors.getDetail(state, selectedContractId || contractId),
  );

  // Loaded for the barcode on its contract document: MyRent only accepts an
  // inspection report for a lease whose registered contract has one, and the
  // contract detail carries the registration's id but not its documents.
  rentRegistrationHooks.useDetail({
    shouldRefetch: false,
    id: selectedContract?.rentRegistrationId,
  });

  const { isBroker } = authHooks.useSelf();

  const legalContact = useSelector((state: IRootStore) =>
    isBroker
      ? contactSelectors.getContactByCustomId(state, EContactCustomId.Legal)
      : undefined,
  );
  const isFetchingLegalContact = useSelector((state: IRootStore) =>
    isBroker
      ? state.contact.contactsById[EContactCustomId.Legal].isFetching
      : false,
  );

  const handleSuccess = (documentPackage: any) => {
    handleFinish(documentPackage);
  };

  const handleFinish = (documentPackage: any) => {
    if (templateDocument && sendToTenant) {
      dispatch(
        templateDocumentsActionsV2.templateDocumentExternalCompletionStart.getAction(
          {
            id: templateDocument.id,
            data: {
              propertyId: templateDocument?.propertyId || 0,
              contractId: templateDocument?.contractId,
              documentId,
              recipients: [ERecipientType.Tenant],
            },
            onSuccess: () => {
              refetchTemplateDocument();

              navigate(
                `/properties/${templateDocument.propertyId}/documents?documentId=${templateDocument.documentId}`,
              );
            },
          },
        ),
      );
    } else {
      if (propertyId && contractId) {
        navigate(
          `/properties/${documentPackage.propertyId}/contracts?documentPackageId=${documentPackage.id}`,
        );

        return;
      }

      if (propertyId) {
        navigate(
          `/properties/${documentPackage.propertyId}/documents?documentPackageId=${documentPackage.id}`,
        );

        return;
      }

      navigate(
        `/follow-up/signatures?page=1&documentPackageId=${documentPackage.id}`,
      );
    }
  };

  if (isBroker && isFetchingLegalContact) {
    return <Loading />;
  }

  if (propertyId && !property) {
    return <Loading />;
  }

  if (contractId && !contract) {
    return <Loading />;
  }

  if (documentId && !documentId) {
    return <Loading />;
  }

  if (document?.templateDocumentId && !templateDocument) {
    return <Loading />;
  }

  const signerFields = sendToTenant ? templateDocument?.signerFields : {};

  const extraData: IExtraData = {
    document,

    contract,
    property,

    templateDocument,

    documentPackageType,

    contactMeMaster,
    legalContact,
    isBroker,

    signerFields,
    sendToTenant,
  };

  return (
    <Page
      title={
        sendToTenant
          ? ts.documentPackageFromSendToTenantHeading()
          : ts.documentPackageFormHeading()
      }
      subtitle={property ? formatAddress(property) : undefined}
      breadcrumbs={getBreadcrumb({ propertyId, documentId })}
    >
      <Card>
        <MultiStepForm
          formId={FORM_ID}
          // schemas={schemas}
          schemas={createDocumentPackageForm(extraData)}
          withSummary={true}
          submitLabel={ts.system("save_send")}
          isPerformingSubmitActionSelector={isPerformingSubmitActionSelector(
            extraData,
          )}
          performingSubmitActionResultSelector={performingSubmitActionResultSelector(
            extraData,
          )}
          performingSubmitActionErrorSelector={performingSubmitActionErrorSelector(
            extraData,
          )}
          submitActionCreator={(formData: any) =>
            submitActionCreator(extraData)(formData)
          }
          onSuccess={handleSuccess}
        />
      </Card>
    </Page>
  );
}, utils.components.arePropsEqual);
